import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Star, 
  Heart, 
  Shield, 
  Quote,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Calendar,
  Award,
  Users,
  CheckCircle,
  Filter,
  Search as SearchIcon
} from 'lucide-react';
import ScrollReveal from '../components/ui/ScrollReveal';
import ScrollRevealStack from '../components/ui/ScrollRevealStack';

const Testimonials = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // SEO optimization
  useEffect(() => {
    document.title = 'Customer Testimonials & Reviews - PetsFostering | Community Feedback';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Read testimonials and reviews from pet owners using PetsFostering marketplace. Discover real experiences connecting with pet caregivers in the community.';

    // Structured data for testimonials
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "PetsFostering",
      "url": "https://PetsFostering.app",
      "description": "Pet care marketplace platform connecting pet owners with independent local caregivers",
      "review": [
        {
          "@type": "Review",
          "author": {
            "@type": "Person",
            "name": "Priya Sharma"
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          },
          "reviewBody": "PetsFostering platform made it easy to find a caregiver! As a working mother, I found Sarah through the platform and she takes great care of my Golden Retriever Max and sends me photos and updates throughout the day."
        },
        {
          "@type": "Review", 
          "author": {
            "@type": "Person",
            "name": "Rahul Verma"
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          },
          "reviewBody": "The platform helped me connect with Anjali who has been caring for my rescue cat Whiskers for 6 months now. Very happy with the connection I made!"
        }
      ]
    };

    let script = document.querySelector('script[type="application/ld+json"][data-testimonials]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-testimonials', 'true');
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"][data-testimonials]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const testimonials = [
    {
      id: 1,
      name: 'Priya Sharma',
      location: 'Mumbai, Maharashtra',
      petName: 'Max',
      petType: 'Golden Retriever',
      serviceUsed: 'Pet Sitting',
      rating: 5,
      duration: '8 months',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      petImage: 'https://images.unsplash.com/photo-1552053831-71594a27632d?w=150&h=150&fit=crop',
      caregiverName: 'Sarah Johnson',
      review: "Found Sarah through PetsFostering platform! As a working mother, the platform made it easy to connect with a caregiver. Sarah takes excellent care of my Golden Retriever Max and sends me photos and updates throughout the day. Max is always excited when he sees Sarah coming!",
      category: 'pet-sitting',
      verified: true,
      helpful: 127
    },
    {
      id: 2,
      name: 'Rahul Verma',
      location: 'Bengaluru, Karnataka',
      petName: 'Whiskers',
      petType: 'Persian Cat',
      serviceUsed: 'Cat Boarding',
      rating: 5,
      duration: '6 months',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      petImage: 'https://images.unsplash.com/photo-1574158622682-e40e69881006?w=150&h=150&fit=crop',
      caregiverName: 'Anjali Patel',
      review: "Used the PetsFostering platform to find Anjali for my rescue cat Whiskers. Anjali has been caring for Whiskers for 6 months now, and I couldn't be happier! Whiskers, who is usually shy around strangers, has formed such a beautiful bond with Anjali. The daily updates and photos are wonderful when I travel for work.",
      category: 'boarding',
      verified: true,
      helpful: 98
    },
    {
      id: 3,
      name: 'Anita Kumar',
      location: 'Delhi, NCR',
      petName: 'Bruno & Bella',
      petType: 'Labrador Mix',
      serviceUsed: 'Dog Walking',
      rating: 5,
      duration: '1 year',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      petImage: 'https://images.unsplash.com/photo-1551717743-49959800b1f6?w=150&h=150&fit=crop',
      caregiverName: 'Michael Chen',
      review: "Having two high-energy Labs, I needed someone for their daily walks. Found Michael through PetsFostering and he's been amazing - picks them up every morning, takes them to different parks, and even sends me GPS tracking of their routes! Bruno and Bella are always tired and happy when they return.",
      category: 'walking',
      verified: true,
      helpful: 156
    },
    {
      id: 4,
      name: 'Sanjay Gupta',
      location: 'Pune, Maharashtra',
      petName: 'Coco',
      petType: 'Poodle',
      serviceUsed: 'Pet Grooming',
      rating: 5,
      duration: '4 months',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      petImage: 'https://images.unsplash.com/photo-1616190265223-6d8c69a2ad8a?w=150&h=150&fit=crop',
      caregiverName: 'Lisa Rodriguez',
      review: "Coco has sensitive skin and I needed the right groomer. Connected with Lisa through PetsFostering and she's been incredible - uses special hypoallergenic products and is so gentle with Coco. The before and after photos she shares show the amazing transformation every time. Coco actually enjoys grooming sessions now!",
      category: 'grooming',
      verified: true,
      helpful: 89
    },
    {
      id: 5,
      name: 'Meera Nair',
      location: 'Chennai, Tamil Nadu',
      petName: 'Shadow',
      petType: 'German Shepherd',
      serviceUsed: 'Pet Training',
      rating: 5,
      duration: '3 months',
      avatar: 'https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=150&h=150&fit=crop&crop=face',
      petImage: 'https://images.unsplash.com/photo-1589941013453-ec89f33b5e95?w=150&h=150&fit=crop',
      caregiverName: 'David Kumar',
      review: "Shadow was becoming increasingly aggressive with other dogs. Found David through PetsFostering and his training approach has been transformational. In just 3 months, Shadow went from being reactive to being calm and sociable. David's positive reinforcement methods and clear communication helped me understand how to continue the training at home.",
      category: 'training',
      verified: true,
      helpful: 134
    },
    {
      id: 6,
      name: 'Kavya Reddy',
      location: 'Hyderabad, Telangana',
      petName: 'Mimi',
      petType: 'Siamese Cat',
      serviceUsed: 'House Sitting',
      rating: 5,
      duration: '5 months',
      avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop&crop=face',
      petImage: 'https://images.unsplash.com/photo-1543946207-39bd91e70ca7?w=150&h=150&fit=crop',
      caregiverName: 'Rachel Singh',
      review: "When I had to travel abroad for work, I needed someone to care for Mimi. Found Rachel through PetsFostering for house-sitting - she stayed at my apartment, maintained Mimi's routine, and even watered my plants! The daily video calls and photos showed me how comfortable and happy Mimi was.",
      category: 'house-sitting',
      verified: true,
      helpful: 112
    }
  ];

  const featuredTestimonials = testimonials.slice(0, 3);
  
  const categories = [
    { id: 'all', name: 'All Reviews', count: testimonials.length },
    { id: 'pet-sitting', name: 'Pet Sitting', count: testimonials.filter(t => t.category === 'pet-sitting').length },
    { id: 'walking', name: 'Dog Walking', count: testimonials.filter(t => t.category === 'walking').length },
    { id: 'boarding', name: 'Pet Boarding', count: testimonials.filter(t => t.category === 'boarding').length },
    { id: 'grooming', name: 'Pet Grooming', count: testimonials.filter(t => t.category === 'grooming').length },
    { id: 'training', name: 'Pet Training', count: testimonials.filter(t => t.category === 'training').length },
    { id: 'house-sitting', name: 'House Sitting', count: testimonials.filter(t => t.category === 'house-sitting').length }
  ];

  const filteredTestimonials = selectedCategory === 'all' 
    ? testimonials 
    : testimonials.filter(t => t.category === selectedCategory);

  // Stats hidden for now - will enable when we have actual data
  // const stats = [
  //   { number: '4.9/5', label: 'Average Rating', icon: Star, color: 'text-yellow-600' },
  //   { number: '15,847', label: 'Total Reviews', icon: Users, color: 'text-blue-600' },
  //   { number: '98%', label: 'Would Recommend', icon: Heart, color: 'text-red-600' },
  //   { number: '50K+', label: 'Community Members', icon: CheckCircle, color: 'text-green-600' }
  // ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % featuredTestimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + featuredTestimonials.length) % featuredTestimonials.length);
  };

  return (
    <div className="pf-page-wrap">
      {/* Hero Section */}
      <header className="pf-section-hero py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="left" className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Real Stories from Pet Owners Using PetsFostering
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100 leading-relaxed">
              Read reviews and feedback from pet owners who have connected with caregivers through our marketplace platform.
            </p>
            {/* Stats hidden for now
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
              Stats will be shown here when we have actual data
            </div>
            */}
          </ScrollReveal>
        </div>
      </header>

      {/* Featured Testimonial Carousel */}
      <section className="py-16 bg-white" aria-labelledby="featured-testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="right" className="text-center mb-12">
            <h2 id="featured-testimonials" className="text-3xl md:text-4xl font-bold text-gray-900">
              Featured Customer Stories
            </h2>
          </ScrollReveal>
          
          <ScrollReveal direction="left">
          <div className="relative max-w-4xl mx-auto">
            <div className="pf-organic-card pf-organic-card--mint p-8 md:p-12">
              <div className="text-center mb-8">
                <Quote className="w-12 h-12 text-[var(--pf-accent)] mx-auto mb-6" />
                <blockquote className="text-xl md:text-2xl text-gray-800 leading-relaxed mb-8 italic">
                  "{featuredTestimonials[currentTestimonial].review}"
                </blockquote>
              </div>

              <div className="flex items-center justify-center gap-6">
                <img 
                  src={featuredTestimonials[currentTestimonial].avatar} 
                  alt={featuredTestimonials[currentTestimonial].name}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <div className="font-bold text-gray-900 text-lg">
                    {featuredTestimonials[currentTestimonial].name}
                  </div>
                  <div className="text-[var(--pf-accent)] font-medium">
                    {featuredTestimonials[currentTestimonial].petName} ({featuredTestimonials[currentTestimonial].petType})
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                    <span className="ml-2 text-sm text-gray-600">
                      {featuredTestimonials[currentTestimonial].location}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation buttons */}
            <button 
              onClick={prevTestimonial}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-[var(--pf-accent)] transition-colors"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button 
              onClick={nextTestimonial}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-[var(--pf-accent)] transition-colors"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Dots indicator */}
            <div className="flex justify-center gap-2 mt-6">
              {featuredTestimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    currentTestimonial === index ? 'bg-[var(--pf-accent)]' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-50" aria-labelledby="filter-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="right">
          <div className="flex items-center justify-between mb-8">
            <h3 id="filter-section" className="text-2xl font-bold text-gray-900">
              Browse Reviews by Service
            </h3>
            <div className="text-sm text-gray-600">
              Showing {filteredTestimonials.length} of {testimonials.length} reviews
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full font-medium transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-[var(--pf-accent)] text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>
          </ScrollReveal>
        </div>
      </section>

      {/* All Testimonials Grid */}
      <main className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollRevealStack className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" staggerMs={75}>
            {filteredTestimonials.map((testimonial) => (
              <article key={testimonial.id} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow h-full">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <h4 className="font-bold text-gray-900 flex items-center gap-1">
                        {testimonial.name}
                        {testimonial.verified && (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        )}
                      </h4>
                      <div className="text-sm text-gray-600 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {testimonial.location}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                </div>

                {/* Service Info */}
                <div className="mb-4 p-3 bg-white rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <img 
                      src={testimonial.petImage} 
                      alt={testimonial.petName}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <div className="text-sm">
                      <span className="font-medium text-gray-900">{testimonial.petName}</span>
                      <span className="text-gray-600"> • {testimonial.petType}</span>
                    </div>
                  </div>
                  <div className="text-xs text-[var(--pf-accent)] font-medium">
                    {testimonial.serviceUsed} • {testimonial.duration} with {testimonial.caregiverName}
                  </div>
                </div>

                {/* Review Content */}
                <blockquote className="text-gray-700 leading-relaxed mb-4 italic">
                  "{testimonial.review}"
                </blockquote>

                {/* Footer */}
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Heart className="w-4 h-4" />
                    {testimonial.helpful} found helpful
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Customer for {testimonial.duration}
                  </div>
                </div>
              </article>
            ))}
          </ScrollRevealStack>
        </div>
      </main>

      {/* Platform Features */}
      <section className="py-16 pf-section-accent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal direction="left" className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Why Pet Owners Use PetsFostering
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Our marketplace platform makes it easy to browse, connect, and manage pet care arrangements 
              with local independent caregivers.
            </p>
          </ScrollReveal>
          
          <ScrollRevealStack className="grid grid-cols-1 md:grid-cols-3 gap-8" staggerMs={120}>
            <div className="text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-white/80" />
              <h3 className="text-xl font-bold mb-2 text-white">Local Community</h3>
              <p className="text-white/90">Connect with independent caregivers in your local area</p>
            </div>
            <div className="text-center">
              <Star className="w-16 h-16 mx-auto mb-4 text-white/80" />
              <h3 className="text-xl font-bold mb-2 text-white">Community Reviews</h3>
              <p className="text-white/90">Read feedback from other pet owners to make informed decisions</p>
            </div>
            <div className="text-center">
              <Heart className="w-16 h-16 mx-auto mb-4 text-white/80" />
              <h3 className="text-xl font-bold mb-2 text-white">Easy Booking Management</h3>
              <p className="text-white/90">Track and organize all your pet care bookings in one place</p>
            </div>
          </ScrollRevealStack>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <ScrollReveal direction="right" className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to Find Local Pet Caregivers?
          </h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            Join our community marketplace and connect with independent caregivers in your area.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/search" 
              className="pf-btn-primary px-8 py-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2"
            >
              <SearchIcon className="w-5 h-5" />
              Find caregivers now
            </Link>
            <Link 
              to="/signup" 
              className="pf-btn-community px-8 py-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2"
            >
              <Heart className="w-5 h-5" />
              Join our community
            </Link>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
};

export default Testimonials;
